import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api.ts";
import { DeleteConfirm } from "../DeleteConfirm.tsx";
import { ErrorBanner } from "../ErrorBanner.tsx";
import { Pagination } from "../Pagination.tsx";
import { useCommunity } from "../community.ts";
import type { ContentSnapshot, SafetyNote } from "../types.ts";
import { SafetyNoteForm } from "./SafetyNoteForm.tsx";

const PAGE_SIZE = 25;

export function SafetyNotes() {
  const community = useCommunity();
  const [items, setItems] = useState<ContentSnapshot[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SafetyNote | null>(null);
  const [deleting, setDeleting] = useState<ContentSnapshot | null>(null);

  async function openEdit(id: string) {
    setBusy(`load-${id}`);
    try {
      const full = await api<SafetyNote>(
        `/admin/safety-notes/${encodeURIComponent(id)}`,
      );
      setEditing(full);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      if (community) params.set("community_id", community);
      const data = await api<{ items: ContentSnapshot[]; total: number }>(
        `/admin/safety-notes?${params.toString()}`,
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [offset, community]);

  useEffect(() => {
    setOffset(0);
  }, [community]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleHide(id: string, hidden: boolean) {
    const action = hidden ? "unhide" : "hide";
    setBusy(`${id}-${action}`);
    try {
      await api(
        `/admin/content/safety-note/${encodeURIComponent(id)}/${action}`,
        { method: "POST" },
      );
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="view-head">
        <h2>Safety notes</h2>
        <span className="view-meta">
          Community-reported safety patterns and warnings.
        </span>
      </div>
      <div className="view-actions">
        <button
          type="button"
          className="luna-btn luna-btn-primary luna-btn-sm"
          onClick={() => setCreating(true)}
          disabled={!community}
          title={
            community
              ? "Add a new safety note"
              : "Pick a community to add a safety note"
          }
        >
          + New safety note
        </button>
      </div>
      {!community && (
        <div className="community-required">
          Pick a community above to add a new safety note.
        </div>
      )}
      {error && <ErrorBanner code={error} />}
      {loading && items.length === 0 ? (
        <div className="spinner-block">Loading safety notes…</div>
      ) : items.length === 0 ? (
        <div className="card">
          <div className="empty">
            <strong>No safety notes in this scope</strong>
            <span>Nothing has been reported in the selected community.</span>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="luna-table luna-table-hover">
            <thead>
              <tr>
                <th>Note</th>
                <th>Community</th>
                <th>Pattern</th>
                <th>Author</th>
                <th>Created</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id}>
                  <td>
                    <a
                      href={`#content/safety-note/${encodeURIComponent(n.id)}`}
                    >
                      #{n.id}
                    </a>
                  </td>
                  <td className="meta">#{n.communityId}</td>
                  <td>{n.title}</td>
                  <td className="meta">user #{n.authorUserId}</td>
                  <td className="meta">{n.createdAt}</td>
                  <td>
                    {n.hidden ? (
                      <span className="luna-badge luna-badge-error">
                        hidden
                      </span>
                    ) : (
                      <span className="luna-badge luna-badge-success">
                        live
                      </span>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="luna-btn luna-btn-outline luna-btn-sm"
                      onClick={() => void toggleHide(n.id, n.hidden)}
                      disabled={busy !== null}
                    >
                      {busy === `${n.id}-${n.hidden ? "unhide" : "hide"}`
                        ? "…"
                        : n.hidden
                          ? "Unhide"
                          : "Hide"}
                    </button>
                    <button
                      type="button"
                      className="luna-btn-row-edit"
                      onClick={() => void openEdit(n.id)}
                      disabled={busy !== null}
                    >
                      {busy === `load-${n.id}` ? "…" : "Edit"}
                    </button>
                    <button
                      type="button"
                      className="luna-btn-row-delete"
                      onClick={() => setDeleting(n)}
                      disabled={busy !== null}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        total={total}
        limit={PAGE_SIZE}
        offset={offset}
        onChange={setOffset}
        loading={loading}
        label="Safety notes"
      />
      {creating && community && (
        <SafetyNoteForm
          communityId={community}
          initial={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
      {editing && (
        <SafetyNoteForm
          communityId={editing.communityId}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
      {deleting && (
        <DeleteConfirm
          title={`Delete safety note #${deleting.id}`}
          description={`Delete the safety note “${deleting.title}”?`}
          endpoint={`/admin/safety-notes/${encodeURIComponent(deleting.id)}`}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}
    </>
  );
}
